import React from 'react';
import styled from '@emotion/styled';
import Button from '../../../../atoms/buttons/Button';
import { EditRounded } from '@material-ui/icons';
import { SvgIcon } from '@material-ui/core';
import { Link } from 'react-router-dom';
import Avatar from '../../../../atoms/Avatar';
import { getButtonTheme, getTextColor } from '../../../../style';
import { isDarkMode } from '../../../../utils/helpers';
import { auth10tixVerification } from '../../../../config/Master';
import useMixPanel from '../../../../hooks/useMixPanel';
import ArrowLeft from '../../../../atoms/icons/ArrowLeft';
import useMediaBreakpoints from '../../../../hooks/useMediaBreakpoints';
import { FONT_FAMILIES } from '../../../../theme/material/constants';
import VerifiedBadgeTooltip from '../../../common/VerifiedBadgeTooltip';
import breakpoints from 'theme/material/breakpoints';

const HeaderContainer = styled('div')({
  padding: '10px 20px',
  height: 300,
});

const smallButtonsIcon = {
  fontSize: 14,
};

const getButtonStyles = () => {
  const themeDarkMode = isDarkMode();

  return {
    border: themeDarkMode ? '1px solid #6c6c6c' : '1px solid #2B8FD7',
    color: themeDarkMode ? '#F6F4F4' : '#2B8FD7',
    background: getButtonTheme().MenuGreyAndWhite,
    boxShadow: themeDarkMode ? '1px 1px 4px rgba(4, 26, 42, 0.75)' : '',
    ':hover': {
      background: !themeDarkMode ? '#CAE3F5 !important' : '#041a2a !important',
      border: themeDarkMode ? '1px solid #aaa !important' : '1px solid #2B8FD7 !important',
      color: themeDarkMode ? 'rgb(245, 247, 250) !important' : 'rgb(28,115,177) !important',
    },
    ':active': {
      background: !themeDarkMode ? '#2272AC' : '#041a2a',
      border: themeDarkMode ? '1px solid #aaa' : '1px solid #2272AC',
      color: themeDarkMode ? 'rgb(245, 247, 250)' : '#CAE3F5',
    },
    borderRadius: 4,
  };
};

const ButtonGroupContainer = styled('div')({
  padding: 0,
  display: 'flex',
  justifyContent: 'center',
  [breakpoints.down(479.05)]: {
    textAlign: 'center',
  },
});

const StyledAvatar = styled(Avatar)({
  marginTop: 35,
  marginLeft: 'auto',
  marginRight: 'auto',
});

const AvatarName = styled('p')(() => ({
  fontSize: 28,
  textAlign: 'center',
  marginBlockStart: 20,
  marginBlockEnd: 22,
  marginInlineStart: 0,
  marginInlineEnd: 0,
  fontFamily: FONT_FAMILIES.secondary,
  fontWeight: 400,
  lineHeight: '18px',
  letterSpacing: -0.3,
  ...getTextColor().DARK_4,
}));

const VerifiedBadgeWrapper = styled('span')({
  marginLeft: 4,
  position: 'relative',
  top: 6,
  [breakpoints.down(768.05)]: {
    top: 4,
  },
});

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const BackArrowContainer = styled('div')(() => ({
  position: 'fixed',
  top: 19,
  borderRadius: '50%',
  width: '36px',
  height: '36px',
  boxShadow: '0px 2px 4px 0px #00000029',
  background: isDarkMode() ? '#000000' : '#F6F4F4',
}));

const SidebarHeader = (props: any): any => {
  const { avatar, username, handleSidebarToggle, hashId, isVerified, isSelfieVerified } = props;
  const { trackPageView } = useMixPanel();
  const { isMobile } = useMediaBreakpoints();

  return (
    <HeaderContainer id="sidebar-header">
      <BackArrowContainer style={isMobile ? { right: 22 } : { left: 22 }}>
        <Button
          customStyle={{
            padding: 15,
            height: '36px',
            width: '36px',
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            ':active': {
              borderRadius: '20px !important',
              background:
                (isDarkMode() ? '#212121' : 'linear-gradient(180deg, #E4F8F6 0%, #FFFFFF 100%)!important') +
                ' !important',
            },
          }}
          buttonType="link"
          onClick={() => handleSidebarToggle(false)}
          id="sidebar-close-button"
          data-test-id="sidebar-close-button"
        >
          <ArrowLeft
            color={isDarkMode() ? '#FFFFFF' : '#0A5B91'}
            cssStyle={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: isMobile ? 'translate(-50%, -50%)' : 'translate(-50%, -50%) rotateZ(180deg)',
              transformStyle: 'preserve-3d',
            }}
          />
        </Button>
      </BackArrowContainer>
      <Link
        data-test-id="cy-sidebar-profile-link"
        to={`/profile/${hashId}`}
        style={{ cursor: 'pointer' }}
        onClick={() => handleSidebarToggle(false)}
      >
        <StyledAvatar
          src={avatar}
          alt="profile"
          width={120}
          shape="circle"
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          handleSidebarToggle={handleSidebarToggle}
          sidebar
        />
        <AvatarName>
          {username}
          {((auth10tixVerification && isVerified) || isSelfieVerified) && (
            <VerifiedBadgeWrapper>
              <VerifiedBadgeTooltip showToolTip={false} isSelfieVerified={isSelfieVerified} isIdVerified={isVerified} />
            </VerifiedBadgeWrapper>
          )}
        </AvatarName>
      </Link>
      <ButtonGroupContainer>
        <Button
          id="EditProfile"
          buttonType="secondaryBlue"
          size="small"
          customStyle={getButtonStyles()}
          to={`/profile/${hashId}`}
          onClick={() => {
            handleSidebarToggle(false);
            trackPageView('menu_edit_profile');
          }}
        >
          <SvgIcon component={EditRounded} style={smallButtonsIcon} />
          &nbsp;Edit Profile
        </Button>
      </ButtonGroupContainer>
    </HeaderContainer>
  );
};

export default SidebarHeader;
